import { Icon } from "@2u-landing-pages/components/Icon";
import { useParseMeta } from "@2u-landing-pages/providers";
import { ContentfulImageConfig } from "@2u-landing-pages/types";
import { renderEmbeddedEntry, renderMark, renderText } from "@2u-landing-pages/utils/contentful-richtext";
import EventClient from "@2u-landing-pages/utils/event-client";
import { getGradientOverlayStyle } from "@2u-landing-pages/utils/gradient-overlay-config";
import { getBorderRadius } from "@2u-landing-pages/utils/imageUtils";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { Body, Container, Heading, Subhead } from "@red-digital/bricks";
import cn from "classnames";
import React from "react";
import { ContentfulImage } from "../../../ContentfulImage";
import { CustomCSSProperties, TileVariantProps } from "../../Tile.types";
// @ts-ignore
import styles from "./Default.module.scss";

export const Default: React.FC<TileVariantProps> = ({
  content,
  padded,
  desktopOrientation,
  mobileOrientation,
  desktopLayoutOrientation,
  mobileLayoutOrientation,
  stackedLayoutPadding,
  heading,
  subhead,
  body,
  className,
  style: styleProps,
  textAlign,
  phoneNumber,
  iconSelectionData,
  inspector,
}) => {
  const parseMetadata = useParseMeta();
  const fields = content?.fields || {};
  const style: CustomCSSProperties = { ...styleProps };
  const selectedIcon = iconSelectionData?.icon ?? "";
  const selectedIconSize = iconSelectionData?.size || undefined;
  const hasIcon = Boolean(selectedIcon);

  const imageConfig = fields.image_config as unknown as ContentfulImageConfig;
  const desktopCfg = imageConfig?.default;
  const mobileCfg = imageConfig?.mobile;

  const desktopOverlayStyle = desktopCfg ? getGradientOverlayStyle(desktopCfg, "desktop") : {};
  const mobileOverlayStyle = mobileCfg ? getGradientOverlayStyle(mobileCfg, "mobile") : {};

  if (fields?.background_color) style["--tile-background-color"] = `var(${fields?.background_color})`;
  if (fields?.image_aspect_ratio) style["--tile-image-aspect-ratio"] = fields?.image_aspect_ratio;

  const isSvgImage = fields?.image?.fields?.file?.contentType === "image/svg+xml";

  if (desktopCfg) {
    style["--aspect-ratio-desktop"] =
      desktopCfg.aspectRatio && desktopCfg.aspectRatio !== "original" ? desktopCfg.aspectRatio : undefined;
    style["--focal-point-desktop"] = desktopCfg?.focalPoint ?? "center";
    const desktopBorderRadius = getBorderRadius(desktopCfg?.r, isSvgImage);
    if (desktopBorderRadius !== undefined) {
      style["--border-radius-desktop"] = desktopBorderRadius;
    }
  }
  if (mobileCfg) {
    style["--aspect-ratio-mobile"] =
      mobileCfg.aspectRatio && mobileCfg.aspectRatio !== "original" ? mobileCfg.aspectRatio : undefined;
    style["--focal-point-mobile"] = mobileCfg?.focalPoint ?? "center";
    const mobileBorderRadius = getBorderRadius(mobileCfg?.r, isSvgImage);
    if (mobileBorderRadius !== undefined) {
      style["--border-radius-mobile"] = mobileBorderRadius;
    }
  }

  Object.assign(style, desktopOverlayStyle, mobileOverlayStyle);

  const handleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const anchor = target.tagName === "A" ? target : target.closest("a");

    if (anchor) {
      e.preventDefault();
      const uniqueId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      EventClient.elementClicked({
        webElement: {
          elementType: "Tile Link",
          htmlId: (anchor as HTMLAnchorElement).href,
          location: window.location.href,
          text: (anchor as HTMLAnchorElement).innerText,
          name: uniqueId,
        },
      });

      const urlParams = new URLSearchParams(window.location.search);
      urlParams.append("utm_subId1", uniqueId);
      const redirectUrl = `${(anchor as HTMLAnchorElement).href}?${urlParams.toString()}`;
      window.open(redirectUrl, "_blank");
    }
  };

  return (
    <Container
      flex
      direction={mobileOrientation === "vertical" ? "column" : "row"}
      alignItems="center"
      lg={{
        direction: desktopOrientation === "vertical" ? "column" : "row",
      }}
      style={style}
      className={cn(
        className,
        styles.tile,
        styles[`mobile-${mobileOrientation}`],
        styles[`desktop-${desktopOrientation}`],
        styles[`mobile-layout-${mobileLayoutOrientation}`],
        styles[`desktop-layout-${desktopLayoutOrientation}`],
        styles[`stacked-layout-padding-${stackedLayoutPadding}`],
        {
          [styles.padded]: padded,
          [styles.textAlignLeft]: textAlign === "left",
          [styles.solidOverlay]: [desktopCfg, mobileCfg].some((cfg) => cfg?.gradientType === "solid"),
          [styles.gradientOverlay]: [desktopCfg, mobileCfg].some((cfg) => cfg?.gradientType === "gradient"),
          [styles.badge]: fields?.variation === "badge",
        },
      )}
      onClick={handleClick}
    >
      {(hasIcon || fields.image) && (
        <div
          className={cn(styles.slot, {
            [styles.slotIcon]: hasIcon,
            [styles.slotImage]: fields.image,
            [styles.iconOnly]: !fields.image,
            [styles.imageOnly]: !hasIcon,
          })}
        >
          {!fields.image && hasIcon && (
            <Icon
              name={selectedIcon}
              size={selectedIconSize}
              className={styles.icon}
              {...inspector({
                entryId: content.sys.id,
                fieldId: "iconSelection", // Kludge: Contentful inspector doesn't support snake_case
              })}
            />
          )}
          {fields.image && (
            <ContentfulImage
              image={fields.image}
              config={fields.image_config}
              className={cn(styles.image, {
                [styles.svgImage]: isSvgImage,
              })}
              {...inspector({
                entryId: content.sys.id,
                fieldId: "image",
              })}
              style={style}
            />
          )}
        </div>
      )}
      {fields?.eyebrow &&
        typeof fields?.eyebrow === "object" &&
        documentToReactComponents(fields?.eyebrow, {
          renderMark,
          renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
          renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => (
              <Subhead
                type="span"
                className={styles.eyebrow}
                variant="subhead-sm"
                modifier={500}
                {...inspector({
                  entryId: content.sys.id,
                  fieldId: "eyebrow",
                })}
              >
                {children}
              </Subhead>
            ),
            [INLINES.EMBEDDED_ENTRY]: (node) =>
              renderEmbeddedEntry(node, "Tile Eyebrow", fields?.eyebrow, phoneNumber, parseMetadata),
          },
        })}
      {fields?.heading &&
        typeof fields?.heading === "object" &&
        documentToReactComponents(fields?.heading, {
          renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
          renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => (
              <Heading
                className={styles.heading}
                variant="heading-xl"
                {...heading}
                {...inspector({
                  entryId: content.sys.id,
                  fieldId: "heading",
                })}
              >
                {children}
              </Heading>
            ),
            [INLINES.EMBEDDED_ENTRY]: (node) => {
              const iconSize = !heading?.variant ? "md" : undefined;
              return renderEmbeddedEntry(node, "Tile Heading", fields?.heading, phoneNumber, parseMetadata, iconSize);
            },
          },
        })}
      {fields?.subhead &&
        typeof fields?.subhead === "object" &&
        documentToReactComponents(fields?.subhead, {
          renderMark,
          renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
          renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => (
              <Subhead
                variant="subhead-md"
                {...subhead}
                className={cn(styles.subhead)}
                {...inspector({
                  entryId: content.sys.id,
                  fieldId: "subhead",
                })}
              >
                {children}
              </Subhead>
            ),
            [INLINES.EMBEDDED_ENTRY]: (node) =>
              renderEmbeddedEntry(node, "Tile Subhead", fields?.subhead, phoneNumber, parseMetadata),
          },
        })}
      {fields?.body &&
        documentToReactComponents(fields?.body, {
          renderMark,
          renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
          renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => (
              <Body
                variant="body-md"
                {...body}
                className={cn(styles.body, {
                  [styles.badgeBody]: fields?.variation === "badge",
                })}
                {...inspector({
                  entryId: content.sys.id,
                  fieldId: "body",
                })}
              >
                {children}
              </Body>
            ),
            [INLINES.EMBEDDED_ENTRY]: (node) =>
              renderEmbeddedEntry(node, "Tile Body", fields?.body, phoneNumber, parseMetadata),
          },
        })}
    </Container>
  );
};
